import UnlinkIcon from '@/assets/icons/unlink.svg?react'
import { useFetchUser } from '@/features/authorization/api/useFetchUser'
import { useRemoveUserWallet } from '@/features/stake-sol/api'
import { Button } from '@/ui-kit'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/ui-kit/components/Dialog'
import { formatNumber } from '@/utils'
import { Trans, useLingui } from '@lingui/react/macro'
import clsx from 'clsx'
import { useState } from 'react'

type UnlinkWalletButtonProps = {
  className?: string
  isDisabled?: boolean
}

export const UnlinkWalletButton = ({ className, isDisabled }: UnlinkWalletButtonProps) => {
  const { t } = useLingui()
  const { mutateAsync: removeUserWallet, isPending: isUnlinkLoading } = useRemoveUserWallet()
  const [showUnlinkConfirm, setShowUnlinkConfirm] = useState(false)
  const { data: userData } = useFetchUser(true)

  const handleUnlinkConfirm = () => {
    void removeUserWallet().then(() => setShowUnlinkConfirm(false))
  }

  return (
    <>
      <Button
        variant="bordered-gray"
        className={clsx('text-gray-90 gap-2', className)}
        size="icon"
        onClick={() => setShowUnlinkConfirm(true)}
        isLoading={isUnlinkLoading}
        isDisabled={isDisabled || isUnlinkLoading}
        title={t`Unlink`}
      >
        <UnlinkIcon className="h-[16px] w-[16px] shrink-0" />
      </Button>

      <Dialog open={showUnlinkConfirm} onOpenChange={setShowUnlinkConfirm}>
        <DialogContent className="max-w-[540px]!" showCloseButton>
          <DialogHeader>
            <DialogTitle>
              <Trans>Unlink wallet</Trans>
            </DialogTitle>
            <DialogDescription className="text-default text-white-to-black mt-3">
              <Trans>This will result in:</Trans>
            </DialogDescription>
          </DialogHeader>
          <ul className="text-gray-70 list-disc space-y-1 pl-5">
            <li>
              <div className="flex items-center gap-2">
                Lower job rewards
                <div className="border-orange/20 text-orange rounded-md border border-solid px-3 py-0.5">
                  {formatNumber(userData?.stakingMultiplier || 1, 2)}x → 1x
                </div>
              </div>
            </li>
            <li>
              <div className="mt-2 flex items-center gap-2">
                Fewer workers allowed
                <div className="border-orange/20 text-orange rounded-md border border-solid px-3 py-0.5">
                  {userData?.workerLimit || 1} → 3
                </div>
              </div>
            </li>
          </ul>
          <DialogFooter className="mt-8 w-full gap-2">
            <Button className="w-full" variant="default" onClick={() => setShowUnlinkConfirm(false)}>
              <Trans>Cancel</Trans>
            </Button>
            <Button
              className="w-full"
              variant="bordered"
              onClick={handleUnlinkConfirm}
              isLoading={isUnlinkLoading}
              isDisabled={isUnlinkLoading}
            >
              <Trans>Unlink anyway</Trans>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
